import { getFormattedUnit } from "config";
import ExcelJS from "exceljs";

const htmlToText = (html) => {
  if (!html) return "-";

  // decode HTML entities
  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  let decoded = textarea.value;

  return (
    decoded
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?p>/gi, "\n")
      .replace(/<\/?div>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim() || "-"
  );
};

const applyAutoRowHeight = (
  sheet,
  rowIndex,
  text,
  { mergedCols = 3, columnWidth = 15, minHeight = 22, lineHeight = 15 } = {}
) => {
  if (!text || text === "-") return;

  const approxCharsPerLine = Math.floor(columnWidth * mergedCols * 1.2);
  const lineCount = Math.ceil(text.length / approxCharsPerLine);

  const row = sheet.getRow(rowIndex);
  row.height = Math.max(minHeight, lineCount * lineHeight);
};
const getCleanRemarkText = (html) => {
  const text = htmlToText(html);
  return text === "-" ? "" : text;
};

const getWrappedTextHeight = (
  texts,
  { mergedCols = 3, columnWidth = 15, minHeight = 24, lineHeight = 16 } = {}
) => {
  const approxCharsPerLine = Math.max(
    20,
    Math.floor(columnWidth * mergedCols * 1.15)
  );
  const lineCount = texts.reduce((maxLines, text) => {
    const currentLines = String(text || "")
      .split("\n")
      .reduce(
        (total, line) =>
          total + Math.max(1, Math.ceil(line.length / approxCharsPerLine)),
        0
      );

    return Math.max(maxLines, currentLines);
  }, 1);

  return Math.max(minHeight, lineCount * lineHeight + 8);
};
const applyOuterBorderToRange = (sheet, lastRow) => {
  try {
    const lastCol = sheet.columnCount || 1;

    // Only apply borders if cells exist
    if (lastRow > 0 && lastCol > 0) {
      // Apply borders carefully
      for (let c = 1; c <= lastCol; c++) {
        // Top border
        const topCell = sheet.getRow(1).getCell(c);
        topCell.border = {
          ...topCell.border,
          top: { style: "thin", color: { argb: "FF000000" } },
        };

        // Bottom border
        const bottomCell = sheet.getRow(lastRow).getCell(c);
        if (bottomCell) {
          bottomCell.border = {
            ...bottomCell.border,
            bottom: { style: "thin", color: { argb: "FF000000" } },
          };
        }
      }
    }
  } catch (error) {
    console.warn("Border application error:", error);
  }
};
const getRankNumber = (rank) => {
  if (!rank || rank === "NA") return Infinity; // push to last
  const num = parseInt(rank?.replace(/\D/g, ""), 10);
  return isNaN(num) ? Infinity : num;
};
const hasValidBudget = (rfqData) => {
  const budget = rfqData?.rfqBudget;
  return typeof budget === "number" && !isNaN(budget) && budget > 0;
};

// Add "Other Charges" rows
const chargeLabels = [
  { label: "Transport Charges", key: "transport" },
  { label: "Mathade Charges", key: "mathadeCharges" },
  { label: "Loading Charges", key: "loading" },
  { label: "Unloading Charges", key: "unLoading" },
  { label: "Miscellaneous Charges", key: "miscCharges" },
];

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
};

const getNegotiationOutcome = (supplier) => {
  const quotationSets = supplier?.vendorQuotations?.quotationSets || [];
  const firstQuotation = quotationSets[0];
  const lastQuotation = quotationSets[quotationSets.length - 1];
  const firstTotal =
    (firstQuotation?.totalWithoutGst || 0) +
    (firstQuotation?.othersTotalWithoutGst || 0);
  const lastTotal =
    (lastQuotation?.totalWithoutGst || 0) +
    (lastQuotation?.othersTotalWithoutGst || 0);

  if (firstTotal === lastTotal) return "Not Updated";

  const difference = firstTotal - lastTotal;
  const negotiationPercentage =
    firstTotal > 0 ? ((difference / firstTotal) * 100).toFixed(2) : 0;

  return `Rs. ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(difference)} (${negotiationPercentage}%)`;
};

export const handleDownloadLabourComparisonExcel = async (
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar
) => {
  // Include ALL suppliers (both with and without quotations)
  const allSuppliers = rfqData?.suppliers || [];

  const calculatePercentageDifference = (firstPrice, latestPrice) => {
    if (!firstPrice || firstPrice === 0) return 0;
    return latestPrice - firstPrice;
  };

  // Separate suppliers into two groups: those with quotations and those without
  const suppliersWithQuotations = allSuppliers.filter(
    (supplier) => supplier?.quotationReceived
  );
  const suppliersWithoutQuotations = allSuppliers.filter(
    (supplier) => !supplier?.quotationReceived
  );

  // Sort only the suppliers with quotations by rank
  // const getRankNumber = (rank) => {
  //   if (!rank || rank === "NA") return Infinity; // push to last
  //   const num = parseInt(rank.replace(/\D/g, ""), 10);
  //   return isNaN(num) ? Infinity : num;
  // };

  const sortedSuppliersWithQuotes = [...suppliersWithQuotations].sort(
    (a, b) => {
      const rankA = getRankNumber(a?.vendorQuotations?.rank);
      const rankB = getRankNumber(b?.vendorQuotations?.rank);
      return rankA - rankB;
    }
  );

  // Combine sorted suppliers with quotes and unsorted suppliers without quotes
  const sortedSuppliers = [
    ...sortedSuppliersWithQuotes,
    ...suppliersWithoutQuotations,
  ];

  const l1Total =
    sortedSuppliers[0]?.vendorQuotations?.grandTotalWithoutGst ?? 0;

  const awardedSuppliersAmount = rfqData?.suppliers
    ?.filter((supplier) => supplier.isAwarded)
    .reduce((total, supplier) => {
      const amount = supplier?.closingDetails?.[0]?.amount || 0;
      return total + amount;
    }, 0);

  const diff = rfqData?.rfqBudget - awardedSuppliersAmount;
  const isOverBudget = diff < 0;
  const formattedDiff = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
  }).format(Math.abs(diff));
  const budgetDeviationText = `Budget Deviation: Rs. ${formattedDiff || "NA"} ${
    isOverBudget ? "▲" : "▼"
  }`;

  try {
    setExcelLoading(true);
    setLoadingMessage("Generating Labour Comparison Excel...");

    const workbook = new ExcelJS.Workbook();

    /* ================= LABOUR SUMMARY VIEW ================= */

    const shortLowestLabourRates = {};

    const setAmountCell = (cell, value) => {
      if (
        value === "-" ||
        value === null ||
        value === undefined ||
        isNaN(value)
      ) {
        cell.value = "-";
        cell.alignment = { horizontal: "right", vertical: "middle" };
        return;
      }

      cell.value = Number(value); // THIS fixes string issue
      cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    };

    const addVerticalSeparators = (
      sheet,
      {
        lastRow,
        staticEndCol = 5, // Column E
        vendorStartCol = 6, // Column F
        vendorColSpan = 3, // 3 columns per vendor
        vendorCount,
      }
    ) => {
      const rows = sheet.getRows(1, lastRow);

      // 1️ Border after column E
      rows.forEach((row) => {
        const cell = row.getCell(staticEndCol);
        cell.border = {
          ...cell.border,
          right: { style: "thin" },
        };
      });

      // 2️ Border after each vendor block
      for (let i = 0; i < vendorCount; i++) {
        const endCol = vendorStartCol + i * vendorColSpan + (vendorColSpan - 1);

        rows.forEach((row) => {
          const cell = row.getCell(endCol);
          cell.border = {
            ...cell.border,
            right: { style: "thin" },
          };
        });
      }
    };

    rfqData.labourDetails.forEach((labour) => {
      let minRate = Infinity;

      sortedSuppliers.forEach((supplier) => {
        if (!supplier.quotationReceived) return;

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const item = lastSet?.labourItems?.find((l) => l.id === labour._id);

        if (!item || !item.labourRate) return;

        if (item.labourRate > 0 && item.labourRate < minRate) {
          minRate = item.labourRate;
        }
      });

      shortLowestLabourRates[labour._id] =
        minRate === Infinity ? null : minRate;
    });

    // ---------- CREATE SUMMARY SHEET ----------
    const shortSheet = workbook.addWorksheet("Summary View");

    const TOTAL_VENDOR_COLS = sortedSuppliers.length * 3;

    // ---------- COLUMN WIDTHS ----------
    shortSheet.columns = [
      { width: 5 }, // Sr
      { width: 40 }, // Work Description
      { width: 10 }, // Qty
      { width: 10 }, // Unit
      { width: 15 },  // Budget
      { width: 18 },  // Budget Amount
      { width: 15 }, // Lowest Rate
      ...Array(TOTAL_VENDOR_COLS).fill({ width: 15 }),
    ];

    // ---------- RFQ HEADER ----------
    shortSheet.mergeCells("A1:D1");
    shortSheet.mergeCells("E1:G1");

    shortSheet.getCell("A1").value = `Project Name: ${rfqData.projectName}`;
    shortSheet.getCell("E1").value = `RFQ ID: ${rfqData.rfqId}`;

    shortSheet.mergeCells("A2:D2");
    shortSheet.mergeCells("E2:G2");

    shortSheet.getCell("A2").value = `RFQ Name: ${rfqData?.name}`;
    shortSheet.getCell("E2").value = `Created By: ${rfqData?.requesterName || "-"}`;

    shortSheet.mergeCells("A3:D3");
    shortSheet.mergeCells("E3:G3");

    shortSheet.getCell("A3").value = `RFQ Type: ${rfqData.rfqType}`;
    shortSheet.getCell("E3").value = `Created At: ${formatDate(rfqData.createdAt)}`;

    ["A1", "A2", "A3", "E1", "E2", "E3"].forEach((cell) => {
      shortSheet.getCell(cell).font = { bold: true };
      shortSheet.getCell(cell).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
    });

    // ---------- VENDOR HEADERS ----------
    let shortStartCol = 8;

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      shortSheet.mergeCells(`${startLetter}1:${endLetter}1`);
      shortSheet.mergeCells(`${startLetter}2:${endLetter}2`);

      const businessNameCell = shortSheet.getCell(`${startLetter}1`);
      const contactCell = shortSheet.getCell(`${startLetter}2`);
      const mobile = supplier.contactDetails?.[0]?.mobile ?? "-";
      const email = supplier.contactDetails?.[0]?.email ?? "-";
      const rank = supplier.vendorQuotations?.rank ?? "NA";

      businessNameCell.value = `${supplier.businessName || "-"} (${rank})`;
      contactCell.value = `${mobile} | ${email}`;

      [businessNameCell, contactCell].forEach((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          wrapText: true,
          vertical: "middle",
          horizontal: "center",
        };
      });
    });

    /* ---------- INDENT, RFQ BUDGET, AND VENDOR DEVIATION (ROW 4) ---------- */
    {
      const budgetRowIndex = 4;

      // Label A–E
      shortSheet.mergeCells(`A${budgetRowIndex}:D${budgetRowIndex}`);
      shortSheet.mergeCells(`E${budgetRowIndex}:G${budgetRowIndex}`);
      const indentCell = shortSheet.getCell(`A${budgetRowIndex}`);
      const labelCell = shortSheet.getCell(`E${budgetRowIndex}`);

      indentCell.value = `Indent No.: ${rfqData?.parentsRefId || "-"}`;
      indentCell.font = { bold: true };
      indentCell.alignment = { horizontal: "left", vertical: "middle" };

      labelCell.value = `RFQ Budget: Rs. ${new Intl.NumberFormat(
        "en-IN"
      ).format(rfqData?.rfqBudget || 0)}`;

      labelCell.font = { bold: true };
      labelCell.alignment = {
        horizontal: "left",
        vertical: "middle",
      };
      shortSheet.getRow(budgetRowIndex).height = 24;

      // Vendor values (F onwards)
      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = shortStartCol + supplierIndex * 3;
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(colStart + 2).letter;

        shortSheet.mergeCells(
          `${startLetter}${budgetRowIndex}:${endLetter}${budgetRowIndex}`
        );

        const cell = shortSheet.getCell(`${startLetter}${budgetRowIndex}`);

        if (!supplier.quotationReceived) {
          cell.value = "Quotation not submitted";
          cell.font = { italic: true, color: { argb: "FF6B7280" } };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          return;
        }

        const rfqBudget = rfqData?.rfqBudget ?? 0;
        const totalWithGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
        const totalWithoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;
        const totalGst = totalWithGst - totalWithoutGst;

        const deviation = rfqBudget - totalWithGst + totalGst;
        const isOverBudget = deviation < 0;
        const deviationPercentage = rfqBudget
          ? Math.abs((deviation / rfqBudget) * 100)
          : 0;

        cell.value = `Rs. ${Math.abs(deviation).toLocaleString("en-IN")} (${deviationPercentage.toFixed(2)}%) ${
          isOverBudget ? "▲" : "▼"
        }`;

        cell.font = {
          bold: true,
          color: {
            argb: isOverBudget ? "FFFF0000" : "FF008000",
          },
        };

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };
      });
    }

    // ---------- TABLE HEADERS ----------
    const headerRowIndex = 5;
    const headerRow = shortSheet.getRow(headerRowIndex);

    headerRow.height = 42;

    headerRow.values = [
      "Sr.",
      "Work Description",
      "Qty",
      "Unit",
      "Budget",
      "Budget Amount",
      "Lowest Rate",
    ];

    [1, 2, 3, 4, 5, 6, 7].forEach((c) => {
      headerRow.getCell(c).font = { bold: true };
      headerRow.getCell(c).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
      headerRow.getCell(c).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
    });

    // Vendor sub headers
    sortedSuppliers.forEach((_, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;

      const setHeader = (cell, text) => {
        cell.value = text;
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" },
        };
        cell.alignment = {
          wrapText: true,
          horizontal: "center",
          vertical: "middle",
        };
      };

      setHeader(headerRow.getCell(colStart), "Quoted\nRate");
      setHeader(headerRow.getCell(colStart + 1), "Negotiated\nRate");
      setHeader(headerRow.getCell(colStart + 2), "Negotiated\nAmount");
    });

    // ---------- LABOUR ROWS ----------
    let currentRow = headerRowIndex + 1;

    rfqData.labourDetails.forEach((labour, index) => {
      // 1️ Check if ANY vendor has remark for this labour item
      const hasAnyRemark = sortedSuppliers.some((supplier) => {
        if (!supplier.quotationReceived) return false;

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const item = lastSet?.labourItems?.find((l) => l.id === labour._id);

        return Boolean(getCleanRemarkText(item?.remarks));
      });

      // 2️ Define rows INSIDE loop (scope fix)
      const baseRow = currentRow;
      const row = shortSheet.getRow(baseRow);
      const remarkRow = hasAnyRemark ? shortSheet.getRow(baseRow + 1) : null;

      // const row = shortSheet.getRow(currentRow);

      row.getCell(1).value = index + 1;
      row.getCell(2).value = htmlToText(labour.skill);
      row.getCell(3).value = labour.quantity;
      row.getCell(4).value = getFormattedUnit(labour.unit);
      setAmountCell(row.getCell(5), labour?.budget);
      setAmountCell(row.getCell(6), labour?.budgetAmount);
      setAmountCell(row.getCell(7), shortLowestLabourRates[labour._id]);

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = shortStartCol + supplierIndex * 3;

        if (!supplier.quotationReceived) {
          row.getCell(colStart).value = "-";
          row.getCell(colStart + 1).value = "-";
          row.getCell(colStart + 2).value = "-";
          return;
        }

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const firstSet = supplier.vendorQuotations?.quotationSets?.[0];

        const lastItem = lastSet?.labourItems?.find((l) => l.id === labour._id);
        const firstItem = firstSet?.labourItems?.find(
          (l) => l.id === labour._id
        );

        // ----- Quoted Rate (no highlight) -----
        setAmountCell(row.getCell(colStart), firstItem?.labourRate ?? 0);

        // ----- Negotiation Rate (HIGHLIGHT LOWEST) -----
        const negotiationCell = row.getCell(colStart + 1);
        const negotiationRate = Number(lastItem?.labourRate ?? 0);

        // 1️ Set value first
        setAmountCell(negotiationCell, negotiationRate);

        // 2️ Highlight lowest negotiation rate
        if (
          negotiationRate > 0 &&
          shortLowestLabourRates[labour._id] === negotiationRate
        ) {
          negotiationCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFBBF7B3" }, // SAME GREEN AS MATERIAL
          };
        }

        // ----- Negotiated Amount -----
        setAmountCell(
          row.getCell(colStart + 2),
          lastItem?.priceWithoutGst ?? 0
        );
      });

      if (hasAnyRemark) {
        shortSheet.mergeCells(`A${baseRow + 1}:G${baseRow + 1}`);
        const labelCell = shortSheet.getCell(`A${baseRow + 1}`);
        labelCell.value = "Vendor Remarks:";
        labelCell.font = { bold: true, italic: true };
        labelCell.alignment = { horizontal: "left", vertical: "top" };
        const remarks = [];

        sortedSuppliers.forEach((supplier, supplierIndex) => {
          if (!supplier.quotationReceived) return;

          const colStart = shortStartCol + supplierIndex * 3;
          const lastSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

          const item = lastSet?.labourItems?.find((l) => l.id === labour._id);

          const remarkText = getCleanRemarkText(item?.remarks);
          if (!remarkText) return;

          remarks.push(remarkText);

          const startLetter = shortSheet.getColumn(colStart).letter;
          const endLetter = shortSheet.getColumn(colStart + 2).letter;

          shortSheet.mergeCells(
            `${startLetter}${baseRow + 1}:${endLetter}${baseRow + 1}`
          );
          const cell = shortSheet.getCell(`${startLetter}${baseRow + 1}`);

          cell.value = remarkText;
          cell.font = { italic: true };
          cell.alignment = {
            wrapText: true,
            vertical: "top",
            horizontal: "left",
          };

        });

        remarkRow.height = getWrappedTextHeight(remarks, {
          mergedCols: 3,
          columnWidth: 10,
          minHeight: 32,
          lineHeight: 20,
        }) + 8;
      }

      currentRow += hasAnyRemark ? 2 : 1;
    });

    /* ================= LABOUR SUMMARY TOTALS ================= */

    let summaryStartRow = currentRow + 1;

    /* ---------- LABOUR COST ---------- */
    shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value = "Labour Cost :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cost =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.totalWithoutGst ?? "-";

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        cost
      );
    });

    chargeLabels.forEach((charge) => {
      const hasAnyCharge = sortedSuppliers.some((supplier) => {
        if (!supplier.quotationReceived) return false;
        return (
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp > 0
        );
      });
      const chargeRemarks = sortedSuppliers.map((supplier, supplierIndex) => ({
        supplierIndex,
        text: supplier.quotationReceived
          ? getCleanRemarkText(
              supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
                ?.otherCharges?.[charge.key]?.remark
            )
          : "",
      }));
      const validChargeRemarks = chargeRemarks.filter(({ text }) => !!text);

      if (!hasAnyCharge && validChargeRemarks.length === 0) return;

      summaryStartRow += 1;

      shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value = charge.label;
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = shortStartCol + supplierIndex * 3;
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(colStart + 2).letter;

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const chargeValue =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;

        setAmountCell(
          shortSheet.getCell(`${startLetter}${summaryStartRow}`),
          chargeValue
        );
      });

      if (validChargeRemarks.length > 0) {
        summaryStartRow += 1;

        shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
        const labelCell = shortSheet.getCell(`A${summaryStartRow}`);
        labelCell.value = "Remarks :";
        labelCell.font = { italic: true };
        labelCell.alignment = {
          horizontal: "right",
          vertical: "top",
        };

        chargeRemarks.forEach(({ supplierIndex, text }) => {
          const colStart = shortStartCol + supplierIndex * 3;
          const startLetter = shortSheet.getColumn(colStart).letter;
          const endLetter = shortSheet.getColumn(colStart + 2).letter;

          shortSheet.mergeCells(
            `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
          );

          const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
          cell.value = text || "";
          cell.font = { italic: true };
          cell.alignment = {
            wrapText: true,
            vertical: "top",
            horizontal: "left",
          };
        });

        shortSheet.getRow(summaryStartRow).height = getWrappedTextHeight(
          validChargeRemarks.map(({ text }) => text),
          { mergedCols: 3, columnWidth: 15, minHeight: 24, lineHeight: 16 }
        );
      }
    });

    /* ---------- TOTAL BASIC AMOUNT ---------- */
    summaryStartRow += 1;

    shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value = "Total Basic Amount :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };
    shortSheet.getCell(`A${summaryStartRow}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9D9D9" },
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        supplier?.vendorQuotations?.grandTotalWithoutGst ?? "-"
      );
      shortSheet.getCell(`${startLetter}${summaryStartRow}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" }, // light grey
      };
    });

    /* ---------- TOTAL GST ---------- */
    summaryStartRow += 1;

    shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value = "Total GST :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const withGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
      const withoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

      setAmountCell(
        shortSheet.getCell(`${startLetter}${summaryStartRow}`),
        withGst - withoutGst
      );
    });

    /* ---------- TOTAL QUOTATION VALUE ---------- */
    summaryStartRow += 1;

    shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Total Quotation Value (Incl. GST) :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };
    shortSheet.getCell(`A${summaryStartRow}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9D9D9" },
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      // Merge vendor columns
      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);

      // Set value
      setAmountCell(cell, supplier?.vendorQuotations?.grandTotalWithGst ?? "-");

      // Apply alignment
      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      // APPLY GREY BACKGROUND TO MERGED CELL
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
    });

    /* ---------- DIFFERENCE FROM LOWEST QUOTE ---------- */
    summaryStartRow += 1;

    // Label (A–E)
    shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Difference from Lowest Quote :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    //  L1 = lowest total without GST (must already be calculated)
    // const l1Total =
    //   Math.min(
    //     ...sortedSuppliers
    //       .filter((s) => s.quotationReceived)
    //       .map(
    //         (s) =>
    //           s?.vendorQuotations?.grandTotalWithoutGst ?? Infinity
    //       )
    //   ) || 0;

    // Vendor values (F onwards)
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;

      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);

      if (!supplier.quotationReceived) {
        cell.value = "Quotation Not Submitted";
      } else {
        const currentTotal =
          supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

        if (currentTotal === l1Total) {
          cell.value = "Lowest Quote";
          cell.font = { italic: true };
        } else {
          const diff = currentTotal - l1Total;

          const percent = l1Total > 0 ? (diff / l1Total) * 100 : 0;

          // ₹ + % combined text
          cell.value = `Rs. ${Number(diff).toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          })} (${percent.toFixed(2)}%)`;
        }
      }

      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
        wrapText: true,
      };
    });

    /* ---------- NEGOTIATION OUTCOME ---------- */
    summaryStartRow += 1;
    shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
    shortSheet.getCell(`A${summaryStartRow}`).value =
      "Negotiation Outcome :";
    shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
    shortSheet.getCell(`A${summaryStartRow}`).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = shortStartCol + supplierIndex * 3;
      const startLetter = shortSheet.getColumn(colStart).letter;
      const endLetter = shortSheet.getColumn(colStart + 2).letter;
      shortSheet.mergeCells(
        `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
      );

      const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
      cell.value = supplier.quotationReceived
        ? getNegotiationOutcome(supplier)
        : "Quotation Not Submitted";
      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
        wrapText: true,
      };
    });

    /* ---------- PAYMENT TERMS ---------- */
    const hasAnyPaymentTerms = sortedSuppliers.some((supplier) => {
      if (!supplier.quotationReceived) return false;

      const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

      const text = lastSet?.agreedToPaymentTerms
        ? htmlToText(rfqData?.paymentTerms)
        : htmlToText(lastSet?.counterPaymentTerms);

      return !!text && text !== "-";
    });

    if (hasAnyPaymentTerms) {
      summaryStartRow += 1;

      // Label A–E
      shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value = "Payment Terms :";
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = shortStartCol + supplierIndex * 3;
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(colStart + 2).letter;

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const lastSet = supplier.vendorQuotations?.quotationSets?.slice(-1)[0];

        const text = lastSet?.agreedToPaymentTerms
          ? htmlToText(rfqData?.paymentTerms)
          : htmlToText(lastSet?.counterPaymentTerms) || "-";

        const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
        cell.value = text;
        cell.alignment = {
          wrapText: true,
          vertical: "top",
          horizontal: "left",
        };

        // AUTO HEIGHT (THIS IS THE KEY)
        applyAutoRowHeight(shortSheet, summaryStartRow, text, {
          mergedCols: 3,
          columnWidth: 15,
        });
      });
    }

    /* ---------- VENDOR NOTES ---------- */
    const hasAnyVendorNotes = sortedSuppliers.some((supplier) => {
      if (!supplier.quotationReceived) return false;

      const text =
        supplier.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes;

      return !!text && text !== "-";
    });

    if (hasAnyVendorNotes) {
      summaryStartRow += 1;

      // Label A–E
      shortSheet.mergeCells(`A${summaryStartRow}:G${summaryStartRow}`);
      shortSheet.getCell(`A${summaryStartRow}`).value = "Vendor Notes :";
      shortSheet.getCell(`A${summaryStartRow}`).font = { bold: true };
      shortSheet.getCell(`A${summaryStartRow}`).alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = shortStartCol + supplierIndex * 3;
        const startLetter = shortSheet.getColumn(colStart).letter;
        const endLetter = shortSheet.getColumn(colStart + 2).letter;

        shortSheet.mergeCells(
          `${startLetter}${summaryStartRow}:${endLetter}${summaryStartRow}`
        );

        const text =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";

        const cell = shortSheet.getCell(`${startLetter}${summaryStartRow}`);
        cell.value = text;
        cell.alignment = {
          wrapText: true,
          vertical: "top",
          horizontal: "left",
        };

        // AUTO HEIGHT
        applyAutoRowHeight(shortSheet, summaryStartRow, text, {
          mergedCols: 3,
          columnWidth: 15,
        });
      });
    }

    /* ---------- BORDERS ---------- */
    addVerticalSeparators(shortSheet, {
      lastRow: summaryStartRow,
      staticEndCol: 7,
      vendorStartCol: shortStartCol,
      vendorColSpan: 3,
      vendorCount: sortedSuppliers.length,
    });

    applyOuterBorderToRange(shortSheet, summaryStartRow);

    const sheet = workbook.addWorksheet("Detailed View");

    const setNumericCell = (cell, value) => {
      cell.value = value ?? 0;
      cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "right", vertical: "middle" };
    };

    sheet.columns = [
      { header: "Sr.", key: "sr", width: 5 },
      { header: "Work Description", key: "workDescription", width: 40 },
      { header: "Quantity", key: "quantity", width: 10 },
      { header: "Unit", key: "unit", width: 10 },
      { header: "Budget", key: "budget", width: 15 },
      { header: "Budget Amount", key: "budgetAmount", width: 18 },
    ];

    // Update RFQ header to show both responding and non-responding vendors
    // ---------- RFQ HEADER ----------
sheet.mergeCells("A1:C1");
sheet.mergeCells("D1:F1");

sheet.getCell("A1").value = `Project Name: ${rfqData.projectName}`;
sheet.getCell("D1").value = `RFQ ID: ${rfqData.rfqId}`;

sheet.mergeCells("A2:C2");
sheet.mergeCells("D2:F2");

sheet.getCell("A2").value = `RFQ Name: ${rfqData?.name}`;
sheet.getCell("D2").value = `Created By: ${rfqData?.requesterName || "-"}`;

sheet.mergeCells("A3:C3");
sheet.mergeCells("D3:F3");

sheet.getCell("A3").value = `RFQ Type: ${rfqData.rfqType}`;
sheet.getCell("D3").value = `Created At: ${formatDate(rfqData.createdAt)}`;

["A1", "A2", "A3", "D1", "D2", "D3"].forEach((cell) => {
  sheet.getCell(cell).font = { bold: true };
  sheet.getCell(cell).alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true,
  };
});

// ---------- QUOTATION AND VENDOR COUNTS ----------
sheet.mergeCells("A4:C4");
sheet.mergeCells("D4:F4");

sheet.getCell("A4").value = `Total Quotations: ${suppliersWithQuotations.length}`;
sheet.getCell("D4").value = `Total Invited Vendors: ${allSuppliers.length}`;

["A4", "D4"].forEach((cell) => {
  sheet.getCell(cell).font = { bold: true };
  sheet.getCell(cell).alignment = {
    horizontal: "left",
    vertical: "middle",
  };
});

// ---------- INDENT + RFQ BUDGET ----------
sheet.mergeCells("A5:C5");
sheet.mergeCells("D5:F5");

sheet.getCell("A5").value = `Indent No.: ${
  rfqData?.parentsRefId || "-"
}`;

sheet.getCell("D5").value = `RFQ Budget: Rs. ${new Intl.NumberFormat(
  "en-IN"
).format(rfqData?.rfqBudget || 0)}`;

sheet.getCell("A5").font = { bold: true };
sheet.getCell("D5").font = { bold: true };

sheet.getCell("A5").alignment = {
  horizontal: "left",
  vertical: "middle",
};

sheet.getCell("D5").alignment = {
  horizontal: "left",
  vertical: "middle",
};

sheet.getRow(4).height = 22;
sheet.getRow(5).height = 24;

    sheet.getRow(6).values = ["Sr.", "Work Description", "Quantity", "Unit", "Budget", "Budget Amount",];
    sheet.getRow(6).font = { bold: true };
    sheet.getRow(6).alignment = { vertical: "middle", horizontal: "center" };
    sheet.getRow(6).height = 42;
    sheet.getRow(6).eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD9D9D9" },
      };
    });

    // Find lowest rates only from suppliers who submitted quotations
    const lowestRates = {};
    rfqData?.labourDetails?.forEach((labour) => {
      let minRate = Infinity;
      let minSupplierIndexes = [];

      sortedSuppliersWithQuotes?.forEach((supplier, vendorIndex) => {
        const vendorQuotation =
          supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
        const vendorItem = vendorQuotation?.labourItems?.find(
          (item) => item.id === labour._id
        );

        if (vendorItem && vendorItem.labourRate > 0) {
          if (vendorItem.labourRate < minRate) {
            minRate = vendorItem.labourRate;
            minSupplierIndexes = [vendorIndex];
          } else if (vendorItem.labourRate === minRate) {
            minSupplierIndexes.push(vendorIndex);
          }
        }
      });

      lowestRates[labour._id] = {
        minRate: minRate === Infinity ? 0 : minRate,
        minSupplierIndexes,
      };
    });

    let detailedCurrentRow = 7;
    const detailedLabourRows = rfqData.labourDetails.map((labour, index) => {
      const vendorRemarks = sortedSuppliers.map((supplier) => {
        if (!supplier.quotationReceived) return "";

        const latestSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
        const item = latestSet?.labourItems?.find((detail) => detail.id === labour._id);
        return getCleanRemarkText(item?.remarks);
      });
      const itemRow = detailedCurrentRow++;
      const remarkRow = vendorRemarks.some(Boolean) ? detailedCurrentRow++ : null;

      return { labour, index, itemRow, remarkRow, vendorRemarks };
    });

    detailedLabourRows.forEach(({ labour, index, itemRow }) => {
      const row = sheet.getRow(itemRow);

      row.getCell(1).value = index + 1;
      row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      row.getCell(2).value = htmlToText(labour.skill);
      row.getCell(2).alignment = { horizontal: "left", vertical: "middle" };
      setNumericCell(row.getCell(3), Number(labour.quantity));
      row.getCell(4).value = getFormattedUnit(labour.unit);
      row.getCell(4).alignment = { horizontal: "center", vertical: "middle" };
      setAmountCell(row.getCell(5), labour?.budget);
      setAmountCell(row.getCell(6), labour?.budgetAmount);
    });

    // Add supplier columns
    let startCol = 7; // Column E
    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = startCol + supplierIndex * 5;
      const colEnd = colStart + 4;
      const colLetter = sheet.getColumn(colStart).letter;
      const vendorEndLetter = sheet.getColumn(colEnd).letter;
      sheet.mergeCells(`${colLetter}1:${vendorEndLetter}1`);
      sheet.mergeCells(`${colLetter}2:${vendorEndLetter}2`);

    let budgetDeviationLine = "";
let budgetDeviationColor;

if (hasValidBudget(rfqData) && supplier.quotationReceived) {
  const rfqBudget = Number(rfqData?.rfqBudget ?? 0);

  const totalWithGst = Number(
    supplier?.vendorQuotations?.grandTotalWithGst ?? 0
  );

  const totalWithoutGst = Number(
    supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0
  );

  const totalGst = totalWithGst - totalWithoutGst;

  // Same deviation calculation as Summary View
  const deviation = rfqBudget - totalWithGst + totalGst;

  const isOverBudget = deviation < 0;

  const deviationPercentage = rfqBudget
    ? Math.abs((deviation / rfqBudget) * 100)
    : 0;

  budgetDeviationColor = isOverBudget
    ? "FFFF0000"
    : "FF008000";

  budgetDeviationLine = `Rs. ${Math.abs(
    deviation
  ).toLocaleString("en-IN")} (${deviationPercentage.toFixed(2)}%) ${
    isOverBudget ? "▲" : "▼"
  }`;
}

      // Different header for suppliers without quotations
      // Vendor header - same format as Summary View
const mobile = supplier.contactDetails?.[0]?.mobile ?? "-";
const email = supplier.contactDetails?.[0]?.email ?? "-";
const rank = supplier.vendorQuotations?.rank ?? "NA";

const businessNameCell = sheet.getCell(`${colLetter}1`);
const contactCell = sheet.getCell(`${colLetter}2`);

businessNameCell.value = `${supplier.businessName || "-"} (${rank})`;
contactCell.value = `${mobile} | ${email}`;

[businessNameCell, contactCell].forEach((cell) => {
  cell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  cell.font = { bold: true };
});

// Budget deviation - Row 4
sheet.mergeCells(
  `${colLetter}5:${sheet.getColumn(colEnd).letter}5`
);

const deviationCell = sheet.getCell(`${colLetter}5`);

deviationCell.value = budgetDeviationLine || "-";

deviationCell.font = {
  bold: true,
  color: {
    argb: budgetDeviationColor || "FF000000",
  },
};

deviationCell.alignment = {
  vertical: "middle",
  horizontal: "center",
  wrapText: true,
};

// Set column headers
sheet.getRow(6).getCell(colStart).value = "Current Rate";
      sheet.getRow(6).getCell(colStart + 1).value = "First Rate";
      sheet.getRow(6).getCell(colStart + 2).value = "Difference";
      sheet.getRow(6).getCell(colStart + 3).value = "GST";
      sheet.getRow(6).getCell(colStart + 4).value = "Basic Amount";
      [0, 1, 2, 3, 4].forEach((offset) => {
        const headerCell = sheet.getRow(6).getCell(colStart + offset);
        headerCell.font = { bold: true };
        headerCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD9D9D9" },
        };
        headerCell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });

      // Set column widths
      [12, 12, 12, 8, 15].forEach(
        (w, i) => (sheet.getColumn(colStart + i).width = w)
      );

      // Populate labour rates for each supplier
      detailedLabourRows.forEach(({ labour, itemRow, remarkRow, vendorRemarks }) => {
        const row = sheet.getRow(itemRow);

        if (supplier.quotationReceived) {
          const lastQuotationSet =
            supplier.vendorQuotations?.quotationSets?.slice(-1)[0];
          const firstQuotationSet =
            supplier.vendorQuotations?.quotationSets?.[0];
          const quotedLabour = lastQuotationSet?.labourItems?.find(
            (l) => l.id === labour._id
          );
          const firstQuotedLabour = firstQuotationSet?.labourItems?.find(
            (l) => l.id === labour._id
          );

          if (quotedLabour) {
            // Current Rate
            setNumericCell(row.getCell(colStart), quotedLabour.labourRate);

            // First Rate
            const firstRate = firstQuotedLabour?.labourRate || 0;
            setNumericCell(
              row.getCell(colStart + 1),
              firstRate > 0 ? firstRate : "-"
            );

            // Negotiation (difference between first and current rate)
            const percentageDifference = calculatePercentageDifference(
              firstRate,
              quotedLabour.labourRate
            );
            const displayDifference = Math.abs(percentageDifference);
            const negotiationCell = row.getCell(colStart + 2);
            setNumericCell(negotiationCell, displayDifference);

            // Style negotiation cell
            if (percentageDifference < 0) {
              negotiationCell.font = { color: { argb: "FF00AA00" } }; // Green
            } else if (percentageDifference > 0) {
              negotiationCell.font = { color: { argb: "FFFF0000" } }; // Red
            }

            // GST
            const gst = parseFloat(quotedLabour.gst) || 0;
            row.getCell(colStart + 3).value = gst / 100;
            row.getCell(colStart + 3).numFmt = "0.00%";
            row.getCell(colStart + 3).alignment = { horizontal: "right" };

            // Basic Amount
            setNumericCell(
              row.getCell(colStart + 4),
              quotedLabour.priceWithoutGst
            );

            // Highlight lowest rate
            const isLowest =
              lowestRates[labour._id]?.minSupplierIndexes.includes(
                supplierIndex
              );
            if (isLowest) {
              row.getCell(colStart).style = {
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFBBF7B3" }, // Light green
                },
              };
            }
          }
        } else {
          // For vendors who didn't submit quotations, leave all cells empty with "-"
          for (let i = 0; i < 5; i++) {
            row.getCell(colStart + i).value = "-";
            row.getCell(colStart + i).alignment = { horizontal: "center" };
          }
        }

        const vendorRemark = vendorRemarks[supplierIndex];
        if (remarkRow && vendorRemark) {
          const startLetter = sheet.getColumn(colStart).letter;
          const endLetter = sheet.getColumn(colStart + 4).letter;
          sheet.mergeCells(`${startLetter}${remarkRow}:${endLetter}${remarkRow}`);
          const remarkCell = sheet.getCell(`${startLetter}${remarkRow}`);
          remarkCell.value = vendorRemark;
          remarkCell.font = { italic: true };
          remarkCell.alignment = {
            horizontal: "left",
            vertical: "top",
            wrapText: true,
          };
        }
      });

      sheet.getRow(1).height = 20;
    });

    const rfqInfoLineCount = String(sheet.getCell("A1").value || "").split("\n").length;
    sheet.getRow(1).height = Math.max(
      sheet.getRow(1).height || 0,
      rfqInfoLineCount * 18
    );

    detailedLabourRows.forEach(({ remarkRow, vendorRemarks }) => {
      if (!remarkRow) return;

      sheet.mergeCells(`B${remarkRow}:F${remarkRow}`);
      const labelCell = sheet.getCell(`B${remarkRow}`);
      labelCell.value = "Vendor Remarks:";
      labelCell.font = { bold: true, italic: true };
      labelCell.alignment = { horizontal: "left", vertical: "top" };
      sheet.getRow(remarkRow).height = getWrappedTextHeight(vendorRemarks, {
        mergedCols: 5,
        columnWidth: 12,
        minHeight: 32,
        lineHeight: 18,
      }) + 8;
    });

    const labourCostRowIndex = detailedCurrentRow + 1;
    const labourCostRow = sheet.getRow(labourCostRowIndex);
    sheet.mergeCells(`A${labourCostRowIndex}:F${labourCostRowIndex}`);
    labourCostRow.getCell(1).value = "Labour Cost:";
    labourCostRow.getCell(1).font = { bold: true };
    labourCostRow.getCell(1).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 7 + supplierIndex * 5;
      const cost =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
          ?.totalWithoutGst ?? 0;
      setNumericCell(labourCostRow.getCell(colStart + 4), cost);
    });

    const enabledCharges = chargeLabels.filter(
      (charge) => rfqData?.otherCharges?.[charge.key]
    );

    enabledCharges.forEach((charge, index) => {
      const rowIndex = labourCostRowIndex + index + 1;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:F${rowIndex}`);
      row.getCell(1).value = charge.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "right", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 7 + supplierIndex * 5;
        const chargeMRP =
          supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]
            ?.otherCharges?.[charge.key]?.mrp ?? 0;
        setNumericCell(row.getCell(colStart + 4), chargeMRP);
      });
    });

    // Add Summary Rows (Basic Amount, GST, Total, Payment Terms)
    const summaryStartRowIndex = labourCostRowIndex + enabledCharges.length + 1;
    const supplierDetailsLabels = [
      {
        label: "Total Basic Amount:",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithoutGst,
      },
      {
        label: "Total GST:",
        getValue: (s) => {
          const withoutGst = s?.vendorQuotations?.grandTotalWithoutGst || 0;
          const withGst = s?.vendorQuotations?.grandTotalWithGst || 0;
          const gstAmount = withGst - withoutGst;
          return isNaN(gstAmount) ? 0 : gstAmount;
        },
      },
      {
        label: "Total Quotation Amount (Inc. GST):",
        getValue: (s) => s?.vendorQuotations?.grandTotalWithGst,
      },
    ];

    supplierDetailsLabels.forEach((detail, index) => {
      const rowIndex = summaryStartRowIndex + index;
      const row = sheet.getRow(rowIndex);
      sheet.mergeCells(`A${rowIndex}:F${rowIndex}`);
      row.getCell(1).value = detail.label;
      row.getCell(1).font = { bold: true };
      row.getCell(1).alignment = { horizontal: "right", vertical: "middle" };

      sortedSuppliers.forEach((supplier, supplierIndex) => {
        const colStart = 7 + supplierIndex * 5;
        const value = detail.getValue(supplier);
        setNumericCell(row.getCell(colStart + 4), value);
      });
    });

    // Add "Difference from Lowest Quote"
    const diffRowIndex = summaryStartRowIndex + supplierDetailsLabels.length;
    const diffRow = sheet.getRow(diffRowIndex);
    sheet.mergeCells(`A${diffRowIndex}:F${diffRowIndex}`);
    diffRow.getCell(1).value = "Difference from Lowest Quote:";
    diffRow.getCell(1).font = { bold: true };
    diffRow.getCell(1).alignment = { horizontal: "right", vertical: "middle" };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 7 + supplierIndex * 5;
      const grandTotal = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;

      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 4).letter;
      const mergeRange = `${startLetter}${diffRowIndex}:${endLetter}${diffRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${diffRowIndex}`);

      if (supplierIndex === 0) {
        cell.value = "Lowest Quote";
        cell.font = { italic: true };
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else if (!supplier.quotationReceived) {
        cell.value = "No Quote Submitted";
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else {
        const deviation = grandTotal - l1Total;
        const deviationPercent = l1Total
          ? ((deviation / l1Total) * 100).toFixed(2)
          : 0;
        cell.value = `Rs. ${new Intl.NumberFormat("en-IN").format(
          deviation
        )} (${deviationPercent}%)`;
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      }
    });

    const negotiationRowIndex = diffRowIndex + 1;
    const negotiationRow = sheet.getRow(negotiationRowIndex);
    sheet.mergeCells(`A${negotiationRowIndex}:F${negotiationRowIndex}`);
    negotiationRow.getCell(1).value = "Negotiation Outcome:";
    negotiationRow.getCell(1).font = { bold: true };
    negotiationRow.getCell(1).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 7 + supplierIndex * 5;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 4).letter;
      sheet.mergeCells(
        `${startLetter}${negotiationRowIndex}:${endLetter}${negotiationRowIndex}`
      );

      const cell = sheet.getCell(`${startLetter}${negotiationRowIndex}`);
      cell.value = supplier.quotationReceived
        ? getNegotiationOutcome(supplier)
        : "No Quote Submitted";
      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });

    // Add "Payment Terms" row
    const paymentTermsRowIndex = negotiationRowIndex + 1;
    const paymentRow = sheet.getRow(paymentTermsRowIndex);
    sheet.mergeCells(`A${paymentTermsRowIndex}:F${paymentTermsRowIndex}`);
    paymentRow.getCell(1).value = "Payment Terms:";
    paymentRow.getCell(1).font = { bold: true };
    paymentRow.getCell(1).alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 7 + supplierIndex * 5;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 4).letter;
      const mergeRange = `${startLetter}${paymentTermsRowIndex}:${endLetter}${paymentTermsRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${paymentTermsRowIndex}`);
      const lastQuotationSet =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
      if (lastQuotationSet?.agreedToPaymentTerms) {
        cell.value = htmlToText(rfqData?.paymentTerms) || "-";
      } else {
        cell.value = htmlToText(lastQuotationSet?.counterPaymentTerms) || "-";
      }
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    // Add "Vendor Notes" row
    const vendorNotesRowIndex = paymentTermsRowIndex + 1;
    const notesRow = sheet.getRow(vendorNotesRowIndex);
    sheet.mergeCells(`A${vendorNotesRowIndex}:F${vendorNotesRowIndex}`);
    notesRow.getCell(1).value = "Vendor Notes:";
    notesRow.getCell(1).font = { bold: true };
    notesRow.getCell(1).alignment = { horizontal: "right", vertical: "middle" };

    sortedSuppliers.forEach((supplier, supplierIndex) => {
      const colStart = 7 + supplierIndex * 5;
      const startLetter = sheet.getColumn(colStart).letter;
      const endLetter = sheet.getColumn(colStart + 4).letter;
      const mergeRange = `${startLetter}${vendorNotesRowIndex}:${endLetter}${vendorNotesRowIndex}`;
      sheet.mergeCells(mergeRange);
      const cell = sheet.getCell(`${startLetter}${vendorNotesRowIndex}`);
      cell.value =
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.notes || "-";
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    const termsSheet = workbook.addWorksheet("Terms & Conditions");

    // Set header and terms content
    termsSheet.getCell("A1").value = "Terms & Conditions";
    termsSheet.getCell("A1").font = { bold: true };
    termsSheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    termsSheet.getCell("A2").value = htmlToText(rfqData?.termsAndConditions);
    termsSheet.getCell("A2").alignment = { wrapText: true };
    termsSheet.getColumn(1).width = 100;

    const buffer = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buffer]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = `${rfqData?.name?.substring(0, 25) || "Export"}_${
      rfqData?.rfqType || ""
    }_${rfqData?.rfqId || ""}.xlsx`;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("Excel downloaded successfully!", {
      variant: "success",
      autoHideDuration: 3000,
    });
  } catch (error) {
    console.error("Error generating labour comparison Excel:", error);
    enqueueSnackbar("Failed to download Excel file.", {
      variant: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};
